import {
  faArrowLeft,
  faExclamationCircle,
  faLock,
  faQuestionCircle,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import { QueryObserverOptions } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";

import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { CaseBlockScrollProvider } from "src/cases/caseView/CaseBlockScrollContext";
import { CaseBlocks } from "src/cases/caseView/CaseBlocks";
import { CasePageHeaderSideContent } from "src/cases/caseView/CasePageHeaderSideContent";
import { CasePageHeaderTitle } from "src/cases/caseView/CasePageHeaderTitle";
import { CaseSidebar } from "src/cases/caseView/CaseSidebar";
import { TaskWindowManager } from "src/cases/caseView/TaskWindowManager";
import { useCase } from "src/cases/queries/cases";
import { useCaseQueue } from "src/cases/queries/queues";
import { CaseResponse } from "src/clients/case-management/api";
import { EmptyState } from "src/design-system/EmptyState";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { Environment } from "src/hooks/useEnvironment";
import { PageHeader } from "src/layout/PageHeader";
import { CasePageParams, getUrlToCaseQueuePage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const pollInterval: QueryObserverOptions<
  CaseResponse,
  AxiosError
>["refetchInterval"] = (query) => (query.state.error ? false : 1000 * 5);

const CasePageContent: React.FC = () => {
  const { orgId, wsId, case_id: caseId } = useParamsDecode<CasePageParams>();

  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
    refetch: refetchCase,
  } = useCase(caseId, {
    // It is enough to set refetchInterval at the top level hook
    // because all the child queries will inherit it
    refetchInterval: pollInterval,
  });

  const templateData = caseData?.data.template_version;

  const { data: queueData } = useCaseQueue(caseData?.data.queue?.id);

  if (caseError) {
    return <CaseLoadingErrorPage error={caseError} onRetry={refetchCase} />;
  }

  return (
    <CaseBlockScrollProvider>
      <TaskWindowManager />
      <div className="flex h-screen flex-col bg-gray-50">
        <PageHeader
          backLink={getUrlToCaseQueuePage({
            orgId,
            wsId,
            queueId: caseData?.data.queue?.id,
            environment: caseData?.data.env as Environment,
          })}
          sideContent={
            <CasePageHeaderSideContent
              caseData={caseData}
              isLoading={caseLoading}
            />
          }
          title={
            <CasePageHeaderTitle
              caseData={caseData}
              isLoading={caseLoading}
              queueName={queueData?.name}
            />
          }
        />
        <div className="relative m-auto flex h-full min-h-0 w-full items-stretch bg-gray-50">
          <div className="h-full min-w-0 flex-1">
            {caseData?.data.case_blocks &&
              templateData?.case_blocks_configs && (
                <CaseBlocks
                  blocks={caseData.data.case_blocks}
                  blocksConfig={templateData?.case_blocks_configs}
                  caseEnv={caseData.data.env}
                />
              )}
          </div>
          <div className="w-1/3 min-w-[22rem] max-w-150 border-gray-200 border-l bg-white">
            <CaseSidebar />
          </div>
        </div>
      </div>
    </CaseBlockScrollProvider>
  );
};

export const CaseViewPage: React.FC = () => {
  useDocumentTitle(["Case Details", "Cases"]);
  return (
    <FloatingWindowsProvider>
      <CasePageContent />
    </FloatingWindowsProvider>
  );
};

type CaseLoadingErrorPageProps = {
  error: unknown;
  onRetry: () => void;
};

type ErrorContent = {
  icon: typeof faExclamationCircle;
  headline: string;
  description: string;
  buttonType: "retry" | "backToQueues" | "none";
};

const getErrorContent = (error: unknown): ErrorContent => {
  if (isAxiosError(error)) {
    const status = error.response?.status;

    switch (status) {
      case 404:
        return {
          icon: faQuestionCircle,
          headline: "Case not found",
          description:
            "The case you're looking for doesn't exist or may have been deleted.",
          buttonType: "backToQueues",
        };
      case 403:
        return {
          icon: faLock,
          headline: "Access denied",
          description:
            "You don't have permission to view this case. Please contact your administrator if you believe this is an error.",
          buttonType: "backToQueues",
        };
      case 409:
        return {
          icon: faExclamationCircle,
          headline: "Case is outdated",
          description:
            "This case was created before breaking changes were made to the case template.",
          buttonType: "backToQueues",
        };
      default:
        break;
    }
  }

  return {
    icon: faExclamationCircle,
    headline: "Error loading case",
    description:
      "We're having trouble loading this case. Please try again later or contact Taktile support if the problem persists.",
    buttonType: "retry",
  };
};

const CaseLoadingErrorPage: React.FC<CaseLoadingErrorPageProps> = ({
  error,
  onRetry,
}) => {
  const { orgId, wsId } = useParamsDecode<CasePageParams>();
  const errorContent = getErrorContent(error);

  const getActionButton = () => {
    switch (errorContent.buttonType) {
      case "retry":
        return {
          text: "Try again",
          icon: faRefresh,
          onClick: onRetry,
        };
      case "backToQueues":
        return {
          text: "Back to queues",
          icon: faArrowLeft,
          linkTo: getUrlToCaseQueuePage({ orgId, wsId }),
        };
      case "none":
      default:
        return undefined;
    }
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <PageHeader
        backLink={getUrlToCaseQueuePage({
          orgId,
          wsId,
        })}
        title="Back to queues"
      />
      <div className="flex h-full w-full items-center justify-center">
        <EmptyState
          actionButton={getActionButton()}
          dataLoc="case-loading-error"
          description={errorContent.description}
          headline={errorContent.headline}
          icon={errorContent.icon}
          variant="error"
        />
      </div>
    </div>
  );
};
